import { Dispatch } from "react";

import {
    AvailabilityRestriction,
    DeclineSettings,
} from "@accurx/api/patient-messaging";
import {
    MessageTemplate,
    PathwayTemplate,
    QuestionnaireTemplate,
} from "@accurx/message-templates";
import {
    AppointmentAvailabilityPeriod,
    SelfbookConfigurationPayload,
} from "@accurx/self-book";
import { ScheduleSendAt } from "domains/message-component/components/Compose/components/ScheduleSend/types/ScheduleSend.types";
import { NHSAdviceLink, TemplateItem } from "domains/message-component/types";

export type MessageEditorId = number;

type IActionMap<Actions = Record<string, unknown>> = {
    [Key in keyof Actions]: Actions[Key] extends undefined
        ? { type: Key; messageEditorId?: MessageEditorId }
        : {
              type: Key;
              payload: Actions[Key];
              messageEditorId?: MessageEditorId;
          };
};
export type IContextActions<T> = IActionMap<T>[keyof IActionMap<T>];

export const ContactMethods = { Mobile: "Mobile", Email: "Email" } as const;

export type ContactDetail = {
    method: keyof typeof ContactMethods;
    value: string;
    additionalInformation?: string;
    origin:
        | "PDS"
        | "EMR"
        | "Conversation"
        | "UserInput"
        | "SubmittedByPatient"
        | "SubmittedByPatientProxy";
};
export type EmptyContactDetail = {
    method: "None";
};

export type AttachmentSource = "PatientMessaging" | "ClinicianMessaging";

type BaseAttachment = {
    id: string;
    name: string;
    /** File size in bytes */
    size?: number;
};

type SuccessfulUploadOrTemplateAttachment = BaseAttachment & {
    origin: "Template" | "Upload";
    status: "success";
    previewUrl?: string;
};

type UploadingAttachment = BaseAttachment & {
    origin: "Upload";
    status: "loading";
};

type ErrorUploadingAttachment = BaseAttachment & {
    origin: "Upload";
    status: "error";
    error: string;
};

export type Attachment =
    | SuccessfulUploadOrTemplateAttachment
    | UploadingAttachment
    | ErrorUploadingAttachment;

export type ComposeTopLevelErrorType = {
    noContactDetails?: "NO_CONTACT_DETAILS";
};
export type ComposeMessageEditorErrorType = {
    // TODO: check these. some errors are for the entire compose, some are for a single message
    messageLength?: "EMPTY_MESSAGE" | "MESSAGE_TOO_LONG";
    placeholderRemains?: "TEMPLATE_PLACEHOLDER_REMAINS";
    questionnaireLoading?: "QUESTIONNAIRE_STILL_LOADING";
    attachmentLoading?: "ATTACHMENT_STILL_LOADING";
    scheduledTime?: "SCHEDULED_TIME_HAS_PASSED";
};

export type ComposeWarning =
    | "ABUSE_WORD"
    | "FAILED_ATTACHMENT"
    | "SAVE_TO_RECORD_WITH_DIFFERENT_EMR_PATIENT"
    | "SCHEDULED_TIME_HAS_PASSED"
    | "CONSENT_STILL_LOADING"
    | "DECLINED_CONSENT"
    | "MIXED_CONSENT"
    | "FORGOT_ALLOW_REPLY";

export type SelfBookLink = {
    appointmentType: SelfbookConfigurationPayload["appointmentType"];
    slotType?: string;
    timeoutDays: number;
    specificClinician?: {
        clinicianGroupId: number;
        clinicianNames: string[];
    }[];
    // Some self book links are for different organisations than the current one
    crossOrgBooking?: {
        targetOdsName: string;
        targetOdsCode: string;
    };
    availabilityRestriction?: AvailabilityRestriction | null;
    appointmentAvailabilityPeriod: AppointmentAvailabilityPeriod;
    declineSettings?: DeclineSettings;
};

export type PendingQuestionnaire = {
    type: "PendingQuestionnaireTemplate";
    value?: undefined;
    pendingValue: QuestionnaireTemplate;
};

type NoTemplate = {
    type: "NoTemplate";
    value?: undefined;
    pendingValue?: undefined;
};

export type TemplateState =
    | ((
          | (Extract<TemplateItem, { type: "QuestionnaireTemplate" }> & {
                warning?: string;
            })
          | Exclude<TemplateItem, { type: "QuestionnaireTemplate" }>
      ) & {
          pendingValue?: undefined;
      })
    | PendingQuestionnaire
    | NoTemplate;

export type ComposeStateType = {
    /** List of messages. Usually there's only one message, except in Pathways where there can be multiple messages */
    messages: MessageEditor[];
    /**
     * This is for presentation of the "Save" checkbox when a user is connected to an EMR,
     * If this is enabled, the message will be saved to the EMR.
     *
     * Non-integrated users won't see the "Save" checkbox as that is controlled by whether canSaveToRecord is enabled/disabled.
     * We safeguard non-integrated users from calling save to record by wrapping all the save to record actions within a canSaveToRecord check.
     */
    isSaveToRecordEnabled: boolean;
    /** Contact details of the patient */
    contactDetails: ContactDetail | EmptyContactDetail;
    /** Whether the self book configuration is open in any of the message editors. Disables some controls. */
    isSelfbookConfigurationOpen: boolean;

    /**
     * The original greeting set when initialising message component
     * Can be edited by the user
     * Used as reference for
     * - validation
     * - to check if the user has made any changes
     * - to reset the message body to the original state
     * */
    originalGreeting: string;
    /**
     * The original signature set when initialising message component
     * Can be edited by the user
     * Used as reference for
     * - validation
     * - to check if the user has made any changes
     * - to reset the signature to the original state
     * */
    originalSignature: string;
    /**
     * The original body set when initialising message component
     * Can be edited by the user
     * Used as reference for
     * - validation
     * - to check if the user has made any changes
     * - to reset the message body to the original state
     */
    originalBody: string;
    /** The selected template by the user */
    template: TemplateState;
    /** Whether the user can select Questionnaire templates */
    canUseQuestionnaires: boolean;
    /** How many attachments is the user allowed to add to each message */
    maxAttachmentCount: number;
    /**
     * The type of message component
     * Used for analytics purposes
     */
    conversationParticipant: "WithHcp" | "WithPatient";
    /** Errors which affect the whole of compose */
    topLevelErrors?: ComposeTopLevelErrorType;
};

export type MessageEditor = {
    /** Used in pathways to label each message editor */
    editorTitle?: string;
    /** The part of the message between the greeting and the signature */
    messageBody: string;
    /** The signature at the end of the message */
    messageSignature: string;

    /** List of attachment files */
    attachments: Attachment[];
    /** "Allow reply" checkbox. Allows patient to reply to the message */
    isPatientResponseEnabled: boolean;
    /** Errors that affect a single message editor */
    messageEditorErrors?: ComposeMessageEditorErrorType;
    isScheduling: boolean;

    /** Schedule the message to be sent at a later time */
    sendAt: ScheduleSendAt;
    /**
     * Self book link to be sent with the message
     * With self-book link patients can book an appointment themselves
     */
    selfBookLink: SelfBookLink | null;
    /** NHS advice link attachment to be sent with the message */
    nhsAdviceLink: NHSAdviceLink | null;
};

/**
 * State value exposed by `useCompose` hook when used inside of
 * a MessagEditorContext
 */
export type SingleMessageEditorComposeStateType = MessageEditor &
    Omit<ComposeStateType, "messages">;

/** Context of compose for the entire compose */
export type ComposeContextType = {
    state: ComposeStateType;
    /** true if any of the initial compose state has been changed */
    readonly isComposing: boolean;
    shouldTryToFetchConsent: boolean;
    dispatch: Dispatch<ComposeActions>;
    templatesComboboxMustOpenDownwards?: boolean;
    isMultipleMessages: boolean;
};

/**
 * Context of compose for each single message editor
 *
 * This context doesn't actually exist and is created by the `useCompose` hook and use by MessageEditor.
 * Each message editor isn't aware of the entire compose.
 * It only knows about its own state and some parts of compose state.
 */
export type MessageEditorContextType = Omit<ComposeContextType, "state"> & {
    state: SingleMessageEditorComposeStateType;
};

export const ComposeActionsTypes = {
    // Contact details actions
    UpdateContactDetails: "UPDATE_CONTACT_DETAILS",

    // Message editor actions
    RemoveMessage: "REMOVE_MESSAGE",

    // Textarea actions
    UpdateMessageBody: "UPDATE_MESSAGE_BODY",
    UpdateMessageSignature: "UPDATE_MESSAGE_SIGNATURE",
    UpdateIsPatientResponseEnabled: "UPDATE_IS_PATIENT_RESPONSE_ENABLED",
    AddMessageTemplate: "ADD_MESSAGE_TEMPLATE",
    AddPathwayTemplate: "ADD_PATHWAY_TEMPLATE",
    AddQuestionnaireTemplate: "ADD_QUESTIONNAIRE_TEMPLATE",
    AddPendingQuestionnaireTemplate: "ADD_PENDING_QUESTIONNAIRE_TEMPLATE",
    RemoveTemplate: "REMOVE_TEMPLATE",
    AddSelfBookLink: "ADD_SELF_BOOK_LINK",
    RemoveSelfBookLink: "REMOVE_SELF_BOOK_LINK",
    AddNHSAdviceLink: "ADD_NHS_ADVICE_LINK",
    RemoveNHSAdviceLink: "REMOVE_NHS_ADVICE_LINK",

    // QuickViews actions
    SetShowSelfBookConfiguration: "SET_SHOW_SELF_BOOK_CONFIGURATION",

    // Attachments actions
    AddAttachments: "ADD_ATTACHMENTS",
    UpdateAttachment: "UPDATE_ATTACHMENT",
    RemoveAttachment: "REMOVE_ATTACHMENT",

    // Save to record settings
    UpdateIsSaveToRecordEnabled: "UPDATE_IS_SAVE_TO_RECORD_ENABLED",

    // Scheduling
    UpdateSendAt: "UPDATE_SEND_AT",

    SetErrors: "SET_ERRORS",
    SetIsScheduling: "SET_IS_SCHEDULING",
} as const;

type ComposePayload = {
    // Contact details actions: switch who the message is going to/in which format (email vs. SMS)
    [ComposeActionsTypes.UpdateContactDetails]: {
        contactDetails: ContactDetail;
    };

    // Message editor actions: remove message (in Pathways)
    [ComposeActionsTypes.RemoveMessage]: undefined;

    // Textarea actions: add / remove / replace content
    [ComposeActionsTypes.UpdateMessageBody]: {
        text: string;
    };
    [ComposeActionsTypes.UpdateMessageSignature]: {
        text: string;
    };
    [ComposeActionsTypes.UpdateIsPatientResponseEnabled]: {
        isPatientResponseEnabled: boolean;
    };
    [ComposeActionsTypes.AddMessageTemplate]: {
        template: MessageTemplate;
    };
    [ComposeActionsTypes.AddPathwayTemplate]: {
        template: PathwayTemplate;
    };
    [ComposeActionsTypes.AddQuestionnaireTemplate]: {
        template: QuestionnaireTemplate;
        warning?: string;
    };
    [ComposeActionsTypes.AddPendingQuestionnaireTemplate]: {
        pendingTemplate: QuestionnaireTemplate;
    };
    [ComposeActionsTypes.RemoveTemplate]: undefined;

    [ComposeActionsTypes.AddSelfBookLink]: { selfBookLink: SelfBookLink };

    [ComposeActionsTypes.RemoveSelfBookLink]: undefined;
    [ComposeActionsTypes.AddNHSAdviceLink]: { nhsAdviceLink: NHSAdviceLink };
    [ComposeActionsTypes.RemoveNHSAdviceLink]: undefined;

    // QuickViews actions
    [ComposeActionsTypes.SetShowSelfBookConfiguration]: { show: boolean };

    // Attachment actions: add / remove attachment
    [ComposeActionsTypes.AddAttachments]: {
        attachments: Extract<Attachment, { status: "success" | "loading" }>[];
    };
    [ComposeActionsTypes.UpdateAttachment]: {
        attachmentId: string;
        attachmentOrigin: Attachment["origin"];
        attachment: Attachment;
    };
    [ComposeActionsTypes.RemoveAttachment]: {
        attachment: Attachment;
    };

    // Scheduling actions
    [ComposeActionsTypes.UpdateSendAt]: { sendAt: ScheduleSendAt };

    // Save to record actions
    [ComposeActionsTypes.UpdateIsSaveToRecordEnabled]: {
        isSaveToRecordEnabled: boolean;
    };

    // Setting errors
    [ComposeActionsTypes.SetErrors]: {
        topLevelErrors: ComposeTopLevelErrorType;
        messageEditorErrors: Record<
            MessageEditorId,
            ComposeMessageEditorErrorType
        >;
    };

    [ComposeActionsTypes.SetIsScheduling]: {
        isScheduling: boolean;
    };
};

export type ComposeActions = IContextActions<ComposePayload>;
